/**
 * Cache utility for banner images and product data
 * Uses localStorage for metadata and Cache API for image blobs
 *
 * Product list strategy (scalability):
 * - Frontend: use cache when present; revalidate (refetch) when expired (30 min TTL).
 * - Backend: on product create / update / status / stock change, calls frontend
 *   revalidate API so server cache is invalidated and next request gets fresh data.
 * - Result: fewer API calls, fresh data after mutations, bounded client staleness (30 min).
 *
 * --- Why we sometimes "wait for backend" despite cache ---
 * - Client cache (localStorage) is browser-only. getServerSideProps runs on the
 *   server, so it cannot use it. Direct visits / refreshes to category or product
 *   pages always hit the backend from the server.
 * - Mitigations: (1) Homepage SWR uses fallbackData from cache + initial props so
 *   we never block render when we have data. (2) Category/product listing SSR
 *   uses Next.js unstable_cache (server-side) so repeat requests for the same
 *   category don't wait on the backend. (3) Client nav uses getCachedData first,
 *   then fetches in background.
 */

const CACHE_KEY_PREFIX = 'banner_cache_';
const CACHE_VERSION_KEY = 'banner_cache_version';

/**
 * Generic data cache utility for product lists and other data
 */
const DATA_CACHE_PREFIX = 'data_cache_';

// Cache TTL (Time To Live) in milliseconds
// 15 minutes — reduces refetch storms; product/category data stays fresh enough for e‑commerce
const CACHE_TTL = 15 * 60 * 1000; // 15 minutes
// 30 minutes for product lists: use cache when present, revalidate (refetch) when expired.
// Backend triggers on-demand revalidate on product create/update/status/stock so server cache stays fresh; client respects this TTL.
const PRODUCT_LIST_CACHE_TTL = 30 * 60 * 1000; // 30 minutes

/**
 * Generate a simple hash/checksum from data for comparison
 * @param {any} data - Data to hash
 * @returns {string} Hash string
 */
const generateDataHash = (data) => {
  try {
    const str = JSON.stringify(data);
    // Simple hash function
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return hash.toString();
  } catch (error) {
    return Date.now().toString(); // Fallback to timestamp
  }
};

/** localStorage key for product-list cache version (backend bumps on create/update/status/stock) */
export const PRODUCT_LIST_VERSION_KEY = "productListCacheVersion";

/** True if cacheKey is for a product list (homepage, shop, category, subcategory) */
const isProductListCacheKey = (cacheKey) =>
  cacheKey === "category" ||
  cacheKey === "bestSelling" ||
  cacheKey === "newArrival" ||
  cacheKey === "discounted" ||
  cacheKey === "reviews" ||
  (typeof cacheKey === "string" && (
    cacheKey.startsWith("shop_") ||
    cacheKey.startsWith("category_") ||
    cacheKey.startsWith("subcategory_") ||
    cacheKey.startsWith("product-")
  ));

/**
 * Check if cached data is expired based on TTL
 * @param {Object} cachedData - Cached data object with cachedAt timestamp
 * @param {string} [cacheKey] - If provided and is a product-list key, uses shorter TTL
 * @returns {boolean} True if cache is expired
 */
const isCacheExpired = (cachedData, cacheKey) => {
  if (!cachedData || !cachedData.cachedAt) return true;
  const age = Date.now() - cachedData.cachedAt;
  const ttl = cacheKey && isProductListCacheKey(cacheKey) ? PRODUCT_LIST_CACHE_TTL : CACHE_TTL;
  return age > ttl;
};

/**
 * Get cached banner data
 * @returns {Object|null} Cached banner object or null
 */
export const getCachedBanner = () => {
  if (typeof window === 'undefined') return null;
  
  try {
    const cachedData = localStorage.getItem(`${CACHE_KEY_PREFIX}data`);
    if (cachedData) {
      return JSON.parse(cachedData);
    }
  } catch (error) {
    console.error('Error reading cached banner:', error);
  }
  return null;
};

/**
 * Cache banner image using Cache API and store metadata in localStorage
 * @param {Object} bannerData - Banner object with imageUrl and other properties
 * @returns {Promise<Object>} Cached banner object with local URL
 */
export const cacheBannerImage = async (bannerData) => {
  if (typeof window === 'undefined' || !bannerData?.imageUrl) return null;

  try {
    // Check if we already have this image cached
    const cachedBanner = getCachedBanner();
    if (cachedBanner?.imageUrl === bannerData.imageUrl) {
      // Same image URL - no need to delete or replace, just return cached version
      return cachedBanner;
    }

    // Only delete old cached image if it exists AND the new image URL is different
    // This ensures we don't delete unnecessarily if it's the same image
    if (cachedBanner?.imageUrl && cachedBanner.imageUrl !== bannerData.imageUrl) {
      await deleteCachedBanner();
    }

    // Fetch and cache the new image
    const response = await fetch(bannerData.imageUrl);
    if (!response.ok) {
      throw new Error('Failed to fetch image');
    }

    const blob = await response.blob();
    const imageUrl = URL.createObjectURL(blob);

    // Store metadata in localStorage
    const cacheData = {
      ...bannerData,
      cachedImageUrl: imageUrl,
      originalImageUrl: bannerData.imageUrl, // Store original URL for fallback
      cachedAt: Date.now(),
    };

    localStorage.setItem(`${CACHE_KEY_PREFIX}data`, JSON.stringify(cacheData));
    localStorage.setItem(`${CACHE_KEY_PREFIX}url`, bannerData.imageUrl);

    return cacheData;
  } catch (error) {
    console.error('Error caching banner image:', error);
    // Fallback to original URL if caching fails
    return bannerData;
  }
};

/**
 * Delete cached banner
 */
export const deleteCachedBanner = async () => {
  if (typeof window === 'undefined') return;

  try {
    const cachedData = getCachedBanner();
    if (cachedData?.cachedImageUrl) {
      // Revoke the object URL to free memory
      URL.revokeObjectURL(cachedData.cachedImageUrl);
    }

    localStorage.removeItem(`${CACHE_KEY_PREFIX}data`);
    localStorage.removeItem(`${CACHE_KEY_PREFIX}url`);
  } catch (error) {
    console.error('Error deleting cached banner:', error);
  }
};

/**
 * Get banner URL - returns cached URL if available, otherwise original URL
 * @param {Object} bannerData - Banner object
 * @returns {string} Image URL
 */
export const getBannerUrl = (bannerData) => {
  if (!bannerData) return null;
  
  const cachedBanner = getCachedBanner();
  
  // If we have cached version and it matches, use cached URL
  if (cachedBanner?.imageUrl === bannerData.imageUrl && cachedBanner?.cachedImageUrl) {
    return cachedBanner.cachedImageUrl;
  }
  
  // Otherwise use original URL
  return bannerData.imageUrl;
};

/**
 * Check if banner URL has changed
 * @param {string} newImageUrl - New banner image URL
 * @returns {boolean} True if banner has changed
 */
export const hasBannerChanged = (newImageUrl) => {
  if (typeof window === 'undefined' || !newImageUrl) return false;
  
  try {
    const cachedUrl = localStorage.getItem(`${CACHE_KEY_PREFIX}url`);
    return cachedUrl !== newImageUrl;
  } catch (error) {
    return true; // Assume changed if we can't check
  }
};

/**
 * Get cached data by key
 * @param {string} cacheKey - Cache key identifier
 * @returns {Object|null} Cached data or null (returns null if expired)
 */
export const getCachedData = (cacheKey) => {
  if (typeof window === 'undefined') return null;
  
  try {
    const cachedData = localStorage.getItem(`${DATA_CACHE_PREFIX}${cacheKey}`);
    if (cachedData) {
      const parsed = JSON.parse(cachedData);
      // Check if cache is expired (product-list keys use shorter TTL so stock changes appear sooner)
      if (isCacheExpired(parsed, cacheKey)) {
        // Delete expired cache
        localStorage.removeItem(`${DATA_CACHE_PREFIX}${cacheKey}`);
        return null;
      }
      return parsed;
    }
  } catch (error) {
    console.error(`Error reading cached data for ${cacheKey}:`, error);
  }
  return null;
};

/**
 * Cache data and compare with existing cache
 * IMPORTANT: Always returns fresh API data to ensure real-time updates
 * Cache is used only for instant initial render, not to block fresh data
 * @param {string} cacheKey - Unique cache key (e.g., 'bestSelling', 'newArrival')
 * @param {any} newData - New data to cache (from API)
 * @returns {Object} Object with { data, hasChanged, cachedData }
 */
export const cacheData = (cacheKey, newData) => {
  if (typeof window === 'undefined' || !newData) {
    return { data: newData, hasChanged: true, cachedData: null };
  }

  try {
    const cachedData = getCachedData(cacheKey); // This already checks expiration
    const newDataHash = generateDataHash(newData);
    
    // Always update cache with fresh API data and return it
    // This ensures new products appear immediately
    const cacheDataObj = {
      data: newData,
      hash: newDataHash,
      cachedAt: Date.now()
    };

    localStorage.setItem(`${DATA_CACHE_PREFIX}${cacheKey}`, JSON.stringify(cacheDataObj));

    // Check if data actually changed (for optimization purposes)
    const hasChanged = !cachedData || cachedData.hash !== newDataHash;

    return {
      data: newData, // Always return fresh API data
      hasChanged,
      cachedData: cachedData?.data || null
    };
  } catch (error) {
    console.error(`Error caching data for ${cacheKey}:`, error);
    return { data: newData, hasChanged: true, cachedData: null };
  }
};

/**
 * Delete cached data by key
 * @param {string} cacheKey - Cache key to delete
 */
export const deleteCachedData = (cacheKey) => {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(`${DATA_CACHE_PREFIX}${cacheKey}`);
  } catch (error) {
    console.error(`Error deleting cached data for ${cacheKey}:`, error);
  }
};

/**
 * Invalidate all product-list-related caches (shop, category, subcategory, homepage lists).
 * Uses the same key format as cacheData/getCachedData: DATA_CACHE_PREFIX + cacheKey (e.g. data_cache_newArrival).
 */
export const invalidateProductListCaches = () => {


  try {
    const prefix = DATA_CACHE_PREFIX; // 'data_cache_'

    // 1. Remove known homepage/list keys by exact full key (same as used in cacheData)
    const knownProductListKeys = ['bestSelling', 'discounted', 'newArrival', 'category', 'reviews'];
    knownProductListKeys.forEach((cacheKey) => {
      localStorage.removeItem(prefix + cacheKey);
    });

    // 2. Remove any other keys whose suffix matches shop_*, category_*, subcategory_*, product-*
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(prefix)) continue;
      const suffix = key.slice(prefix.length);
      if (
        suffix.startsWith('shop_') ||
        suffix.startsWith('category_') ||
        suffix.startsWith('subcategory_') ||
        suffix.startsWith('product-')
      ) {
        toRemove.push(key);
      }
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
  } catch (error) {
    console.error('Error invalidating product list caches:', error);
  }
};

/**
 * Tell other open tabs to invalidate product list caches and refetch (instant update without polling).
 * Call after invalidateProductListCaches() when this tab caused the change (e.g. admin status update).
 * @param {number} [version] - Optional cache version if known (e.g. from backend response).
 */
export const broadcastProductListInvalidated = (version) => {
  if (typeof window === 'undefined') return;
  try {
    const bc = typeof BroadcastChannel !== 'undefined' && new BroadcastChannel('productListCacheInvalidated');
    if (bc) bc.postMessage({ type: 'productListCacheInvalidated', version });
  } catch (_) {}
};

/**
 * Call after product create/update/delete/status change (admin or seller)
 * Invalidates client caches, notifies other tabs, and pings the revalidate API so server cache is cleared.
 */
export const revalidateProductListAfterChange = () => {
  invalidateProductListCaches();
  broadcastProductListInvalidated();
  if (typeof window !== 'undefined') {
    const secret = typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_REVALIDATE_SECRET;
    const url = `${window.location.origin}/api/revalidate-product-cache`;
    const options = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(secret ? { secret } : {}),
    };
    fetch(url, options).catch(() => {});
  }
};

/**
 * Fetch backend product-list cache version; if it increased, invalidate client caches
 * and store new version so home, category, subcategory, shop never show stale data.
 * Call once on app load (e.g. _app mount). In browser uses same-origin /api/cache-version
 * so the version check always hits the backend (no wrong-host or CORS issues).
 * @param {string} [apiBaseUrl] - Backend base URL (used only in SSR; in browser same-origin proxy is used)
 * @returns {Promise<{ invalidated: boolean, version: number }>}
 */
export const checkProductListCacheVersion = async (apiBaseUrl) => {
  if (typeof window === "undefined") return { invalidated: false, version: 0 };

  const versionUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/api/cache-version`
      : apiBaseUrl
        ? `${String(apiBaseUrl).replace(/\/$/, "")}/products/cache-version`
        : null;
  if (!versionUrl) return { invalidated: false, version: 0 };

  try {
    const res = await fetch(versionUrl, {
      method: "GET",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return { invalidated: false, version: 0 };
    const body = await res.json().catch(() => null);
    if (!body || typeof body !== "object") return { invalidated: false, version: 0 };
    const serverVersion = body.productListCacheVersion;
    const stored = localStorage.getItem(PRODUCT_LIST_VERSION_KEY);
    const currentVersion = stored ? parseInt(stored, 10) : 0;
    if (typeof serverVersion === "number" && serverVersion > currentVersion) {
      invalidateProductListCaches();
      localStorage.setItem(PRODUCT_LIST_VERSION_KEY, String(serverVersion));
      if (typeof window !== "undefined" && window.dispatchEvent) {
        window.dispatchEvent(new CustomEvent("productListCacheInvalidated"));
      }
      try {
        const bc =
          typeof BroadcastChannel !== "undefined" &&
          new BroadcastChannel("productListCacheInvalidated");
        if (bc) bc.postMessage({ type: "productListCacheInvalidated", version: serverVersion });
      } catch (_) {}
      return { invalidated: true, version: serverVersion };
    }
    if (typeof serverVersion === "number" && currentVersion === 0) {
      localStorage.setItem(PRODUCT_LIST_VERSION_KEY, String(serverVersion));
    }
    return { invalidated: false, version: serverVersion };
  } catch (e) {
    return { invalidated: false, version: 0 };
  }
};

/**
 * Check if data has changed by comparing hash
 * @param {string} cacheKey - Cache key identifier
 * @param {any} newData - New data to compare
 * @returns {boolean} True if data has changed
 */
export const hasDataChanged = (cacheKey, newData) => {
  if (typeof window === 'undefined' || !newData) return true;

  try {
    const cachedData = getCachedData(cacheKey);
    if (!cachedData) return true;

    const newDataHash = generateDataHash(newData);
    return cachedData.hash !== newDataHash;
  } catch (error) {
    return true; // Assume changed if we can't check
  }
};
